import {
  Post,
  PostManagementLevel,
  PostPriority,
  PostStatus,
  PostType,
  DEFAULT_POST_STATUSES,
  CONFLICT_STATUSES
} from '@eview/core/domain/post/post';
import { AppState } from '@eview/core/store/states/app.state';
import { Store } from '@ngrx/store';
import { Subscription } from 'rxjs';
import { PostHelpers } from '@eview/core/domain/post/post.helpers';
import { selectTenant } from '@eview/core/store/selectors/tenant.selector';
import { selectForms } from '@eview/core/store/selectors/forms.selector';
import { SearchableSelectOption } from '@eview/features/ui/searchable-select/searchable-select.component';
import { FormStatusScheme } from '@eview/core/domain/form/form';
import { take } from 'rxjs/operators';
import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import {
  blockIncidentLongTextKeypress,
  blockIncidentLongTextPaste,
  hasInvalidIncidentLongText
} from '../../validators/incident-long-text.validation';

export type StatusDetailField =
  | 'status_detail_derivado'
  | 'status_detail_cerrado';

export interface StatusDetailChange {
  field: StatusDetailField;
  value: string;
}

@Component({
  selector: 'eview-post-detail-actions',
  templateUrl: 'post-detail-actions.component.html',
  styleUrls: ['post-detail-actions.component.scss']
})
export class PostDetailActionsComponent implements OnInit, OnChanges {
  @Input() public post: Post;
  @Input() showLink: Boolean;
  @Input() showStatus: Boolean;
  @Input() reportStatusEnabled: Boolean;
  @Input() public viewOnlyMode: Boolean;
  /** When false (anonymous), hide classification/status staff meta; keep description. */
  @Input() public showStaffMeta = true;
  @Input() responseLevelAllowed: Boolean;
  @Input() permissionToEdit: Boolean;
  /** Whether role may toggle posts.published (map publish). */
  @Input() public permissionToPublish: Boolean = false;
  @Input() modifyPersonalReport: Boolean;
  @Output() public onChangePriorityClick: EventEmitter<any> = new EventEmitter();
  @Output() public onChangePublishClick: EventEmitter<any> = new EventEmitter();
  @Output() public onLinkToPostClick: EventEmitter<any> = new EventEmitter();
  @Output() public onChangeStatusClick: EventEmitter<any> = new EventEmitter();
  @Output() public onChangeStatusDetailClick: EventEmitter<StatusDetailChange> = new EventEmitter();
  @Output() public onChangeConflictStatusClick: EventEmitter<string> = new EventEmitter();
  @Output() public onChangeManagementLevelClick: EventEmitter<any> = new EventEmitter();
  PostPriority = PostPriority;
  PostStatus = PostStatus;
  PostHelpers = PostHelpers;
  PostType = PostType;
  FormStatusScheme = FormStatusScheme;
  tenantName: string;
  selectedPriority: string;
  selectedConflictStatus: string = null;
  statusDetailDerivadoDraft = '';
  statusDetailCerradoDraft = '';
  statusDetailDerivadoTouched = false;
  statusDetailCerradoTouched = false;
  statusDetailDerivadoEditing = false;
  statusDetailCerradoEditing = false;
  prioritySelectOptions: SearchableSelectOption[] = [
    { value: PostPriority.Low, label: 'PRIORITY_LOW' },
    { value: PostPriority.Medium, label: 'PRIORITY_MEDIUM' },
    { value: PostPriority.High, label: 'PRIORITY_HIGH' },
    { value: PostPriority.Critical, label: 'PRIORITY_CRITICAL' }
  ];
  conflictStatusSelectOptions: SearchableSelectOption[] = CONFLICT_STATUSES.map(
    status => ({
      value: status,
      label: PostHelpers.GetConflictStatusLabel(status)
    })
  );
  postStatuses: PostStatus[] = [...DEFAULT_POST_STATUSES];
  statusSectionLabel = 'INCIDENT_STATUS';
  isOnpeScheme = false;
  postManagementLevels = [
    PostManagementLevel.Unknown,
    PostManagementLevel.Subregional,
    PostManagementLevel.Regional,
    PostManagementLevel.National
  ];
  subs: Subscription;
  constructor(
    private store: Store<AppState>,
    private cdr: ChangeDetectorRef
  ) {
    this.store = store;
    this.subs = new Subscription();
  }

  ngOnInit() {
    this.store.select(selectTenant).subscribe(tenant => {
      this.tenantName = tenant.unique_name;
    });
    if (this.post?.priority) {
      this.selectedPriority = PostHelpers.NormalizePostPriority(this.post.priority);
    }
    this.syncConflictStatus();
    this.syncStatusDetailDrafts();
    this.refreshStatusScheme();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.post) {
      this.refreshStatusScheme();
      this.syncStatusDetailDrafts();
      this.syncConflictStatus();
      if (this.post?.priority) {
        this.selectedPriority = PostHelpers.NormalizePostPriority(this.post.priority);
      }
    }
  }

  private syncConflictStatus(): void {
    this.selectedConflictStatus = this.post?.conflict_status
      ? String(this.post.conflict_status)
      : null;
  }

  private syncStatusDetailDrafts(): void {
    this.statusDetailDerivadoDraft = this.resolveDetailValue(
      this.post?.status_detail_derivado,
      PostStatus.Derivado
    );
    this.statusDetailCerradoDraft = this.resolveDetailValue(
      this.post?.status_detail_cerrado,
      PostStatus.Cerrado
    );
    this.statusDetailDerivadoTouched = false;
    this.statusDetailCerradoTouched = false;
    this.statusDetailDerivadoEditing = false;
    this.statusDetailCerradoEditing = false;
  }

  hasSavedStatusDetailText(field: StatusDetailField): boolean {
    const saved =
      field === 'status_detail_derivado'
        ? this.resolveDetailValue(
            this.post?.status_detail_derivado,
            PostStatus.Derivado
          )
        : this.resolveDetailValue(
            this.post?.status_detail_cerrado,
            PostStatus.Cerrado
          );
    return !!(saved || '').trim();
  }

  /** Prefer dedicated columns; fall back to legacy status_detail for older rows. */
  private resolveDetailValue(
    dedicated: string | null | undefined,
    forStatus: PostStatus
  ): string {
    if (dedicated != null && String(dedicated).length > 0) {
      return String(dedicated);
    }
    if (
      this.post?.status === forStatus &&
      this.post?.status_detail != null &&
      String(this.post.status_detail).length > 0
    ) {
      return String(this.post.status_detail);
    }
    return dedicated != null ? String(dedicated) : '';
  }

  private refreshStatusScheme(): void {
    let scheme = FormStatusScheme.Default;
    const formId = this.post?.form?.id ?? (this.post as any)?.form_id;
    if (formId) {
      this.store
        .select(selectForms)
        .pipe(take(1))
        .subscribe(forms => {
          const match = forms?.results?.find(f => Number(f.id) === Number(formId));
          scheme = PostHelpers.GetStatusSchemeFromForm(match || this.post?.form);
        });
    } else {
      scheme = PostHelpers.GetStatusSchemeFromForm(this.post?.form);
    }
    this.isOnpeScheme = scheme === FormStatusScheme.OnpeMace;
    this.postStatuses = PostHelpers.GetStatusesForScheme(scheme);
    if (this.isOnpeScheme) {
      this.statusSectionLabel = 'ONPE_MACE_STATUS_SECTION';
    } else {
      this.statusSectionLabel =
        PostType.Incident === this.post?.type ? 'INCIDENT_STATUS' : 'RISK_STATUS';
    }
  }

  getStatus(postStatuses, type) {
    return postStatuses;
  }

  get showDerivadoDetailField(): boolean {
    return (
      this.isOnpeScheme &&
      !!this.post?.status &&
      this.post.status === PostStatus.Derivado
    );
  }

  get showCerradoDetailField(): boolean {
    return (
      this.isOnpeScheme &&
      !!this.post?.status &&
      this.post.status === PostStatus.Cerrado
    );
  }

  onStatusButtonClick(status: PostStatus): void {
    this.onChangeStatusClick.emit(status);
  }

  onStatusDetailEdit(field: StatusDetailField): void {
    if (!this.canEditStatusDetail() || (this.post?.parent && this.post.parent.id > 0)) {
      return;
    }
    if (field === 'status_detail_derivado') {
      this.statusDetailDerivadoEditing = true;
      this.statusDetailDerivadoTouched = false;
    } else {
      this.statusDetailCerradoEditing = true;
      this.statusDetailCerradoTouched = false;
    }
  }

  onStatusDetailCancel(field: StatusDetailField): void {
    if (field === 'status_detail_derivado') {
      this.statusDetailDerivadoDraft = this.resolveDetailValue(
        this.post?.status_detail_derivado,
        PostStatus.Derivado
      );
      this.statusDetailDerivadoTouched = false;
      this.statusDetailDerivadoEditing = false;
    } else {
      this.statusDetailCerradoDraft = this.resolveDetailValue(
        this.post?.status_detail_cerrado,
        PostStatus.Cerrado
      );
      this.statusDetailCerradoTouched = false;
      this.statusDetailCerradoEditing = false;
    }
  }

  isStatusDetailEditing(field: StatusDetailField): boolean {
    if (!this.canEditStatusDetail() || (this.post?.parent && this.post.parent.id > 0)) {
      return false;
    }
    const editing =
      field === 'status_detail_derivado'
        ? this.statusDetailDerivadoEditing
        : this.statusDetailCerradoEditing;
    if (editing) {
      return true;
    }
    return !this.hasSavedStatusDetailText(field);
  }

  canShowStatusDetailEdit(field: StatusDetailField): boolean {
    return (
      this.canEditStatusDetail() &&
      !(this.post?.parent && this.post.parent.id > 0) &&
      this.hasSavedStatusDetailText(field) &&
      !this.isStatusDetailEditing(field)
    );
  }

  onStatusDetailSave(field: StatusDetailField): void {
    if (!this.canEditStatusDetail()) {
      return;
    }
    if (!this.isStatusDetailDirty(field)) {
      return;
    }
    const next =
      field === 'status_detail_derivado'
        ? (this.statusDetailDerivadoDraft || '').trim()
        : (this.statusDetailCerradoDraft || '').trim();
    if (!next || hasInvalidIncidentLongText(next)) {
      if (field === 'status_detail_derivado') {
        this.statusDetailDerivadoTouched = true;
      } else {
        this.statusDetailCerradoTouched = true;
      }
      return;
    }
    if (field === 'status_detail_derivado') {
      this.statusDetailDerivadoEditing = false;
      this.statusDetailDerivadoTouched = false;
    } else {
      this.statusDetailCerradoEditing = false;
      this.statusDetailCerradoTouched = false;
    }
    this.onChangeStatusDetailClick.emit({ field, value: next });
  }

  isStatusDetailDirty(field: StatusDetailField): boolean {
    const next =
      field === 'status_detail_derivado'
        ? (this.statusDetailDerivadoDraft || '').trim()
        : (this.statusDetailCerradoDraft || '').trim();
    const prevDedicated =
      field === 'status_detail_derivado'
        ? this.post?.status_detail_derivado
        : this.post?.status_detail_cerrado;
    const prev = prevDedicated != null ? String(prevDedicated).trim() : '';
    return next !== prev;
  }

  hasInvalidStatusDetail(field: StatusDetailField): boolean {
    const value =
      field === 'status_detail_derivado'
        ? this.statusDetailDerivadoDraft
        : this.statusDetailCerradoDraft;
    return hasInvalidIncidentLongText(value);
  }

  isStatusDetailEmpty(field: StatusDetailField): boolean {
    const value =
      field === 'status_detail_derivado'
        ? this.statusDetailDerivadoDraft
        : this.statusDetailCerradoDraft;
    return !(value || '').trim();
  }

  canSaveStatusDetail(field: StatusDetailField): boolean {
    return (
      this.isStatusDetailDirty(field) &&
      !this.isStatusDetailEmpty(field) &&
      !this.hasInvalidStatusDetail(field)
    );
  }

  showStatusDetailCharError(field: StatusDetailField): boolean {
    const touched =
      field === 'status_detail_derivado'
        ? this.statusDetailDerivadoTouched
        : this.statusDetailCerradoTouched;
    return touched && this.hasInvalidStatusDetail(field);
  }

  showStatusDetailRequiredError(field: StatusDetailField): boolean {
    const touched =
      field === 'status_detail_derivado'
        ? this.statusDetailDerivadoTouched
        : this.statusDetailCerradoTouched;
    return touched && this.isStatusDetailEmpty(field);
  }

  onStatusDetailKeypress(event: KeyboardEvent): void {
    blockIncidentLongTextKeypress(event);
  }

  onStatusDetailPaste(event: ClipboardEvent): void {
    blockIncidentLongTextPaste(event);
  }

  onStatusDetailBlur(field: StatusDetailField): void {
    if (field === 'status_detail_derivado') {
      this.statusDetailDerivadoTouched = true;
    } else {
      this.statusDetailCerradoTouched = true;
    }
  }

  canEditStatusDetail(): boolean {
    // Workflow status requires General (edit_any) only.
    return !!this.permissionToEdit && !this.viewOnlyMode;
  }

  /** Criticidad y estado de conflicto: General o Individual sobre informe propio. */
  canEditClassificationMeta(): boolean {
    return (
      (!!this.permissionToEdit || !!this.modifyPersonalReport) &&
      !this.viewOnlyMode
    );
  }

  isActionDisabled(status) {
    if (this.isOnpeScheme) {
      return false;
    }

    const indexOfStatus = this.postStatuses.indexOf(this.post.status);
    const indexOfStatusDisabled = this.postStatuses.indexOf(status);

    if (
      (this.post.status === PostStatus.Verified ||
        this.post.status === PostStatus.Responded ||
        this.post.status === PostStatus.Evaluated) &&
      this.post.published
    ) {
      if (indexOfStatusDisabled < indexOfStatus) {
        return true;
      }
      return false;
    }
    return false;
  }

  canPublish(): boolean {
    return PostHelpers.CanPublishOnMap(this.post?.status);
  }

  /**
   * Publish is independent of edit: permissionToPublish already encodes own vs any.
   */
  canTogglePublish(): boolean {
    if (this.viewOnlyMode || !this.permissionToPublish || !this.canPublish()) {
      return false;
    }
    return true;
  }

  shouldDisplaySection(type?: string) {
    return true;
  }
}